/**
 * Daily leagues (reference: daily competitions; winners move up, the lowest scorers drop, the rest stay).
 *
 * Points: one per heart spent on gifts, music and admiration, plus one per kiss received (at most `kissCap` a day,
 * raised by the "limit" booster). Groups of up to `groupSize` players of the same league are formed through the day.
 */
export const LEAGUE_TIERS = ['wooden', 'bronze', 'silver', 'gold', 'platinum', 'diamond'] as const;
export type LeagueTier = (typeof LEAGUE_TIERS)[number];

export const LEAGUE_ICONS: Record<LeagueTier, string> = {
  wooden: '🪵',
  bronze: '🥉',
  silver: '🥈',
  gold: '🥇',
  platinum: '💠',
  diamond: '💎',
};

export const LEAGUE = {
  groupSize: 30,
  /** Places that move up at the end of the day (not from the top league). */
  promote: 3,
  /** Places that drop at the end of the day, only in groups of at least `demoteMinGroup`. */
  demote: 3,
  demoteMinGroup: 10,
  /** Daily cap on points from received kisses, before "limit" boosters. */
  kissCap: 500,
  /** Points a player needs for the participation gift box. */
  boxMinPoints: 10,
  /** The ×2 booster runs this long. */
  x2Minutes: 5,
  limitStep: 10,
  bonusPoints: 5,
} as const;

export type LeagueChest = 'red' | 'blue' | 'wooden' | 'box';

/** Hearts per chest by league (wooden … diamond); chests also hold wheel chances. */
const CHEST_HEARTS: Record<Exclude<LeagueChest, 'box'>, readonly number[]> = {
  red: [100, 150, 200, 300, 400, 500],
  blue: [50, 75, 100, 150, 200, 250],
  wooden: [25, 40, 50, 75, 100, 125],
};
const CHEST_CHANCES: Record<LeagueChest, number> = { red: 5, blue: 3, wooden: 2, box: 1 };

/** The chest for a final place (1-based) with `points`, or null. */
export function leagueChest(place: number, points: number): LeagueChest | null {
  if (points <= 0) return null;
  if (place === 1) return 'red';
  if (place === 2) return 'blue';
  if (place === 3) return 'wooden';
  return points >= LEAGUE.boxMinPoints ? 'box' : null;
}

export function chestContents(chest: LeagueChest, tier: number): { hearts: number; chances: number } {
  const t = Math.min(Math.max(tier, 0), LEAGUE_TIERS.length - 1);
  return { hearts: chest === 'box' ? 0 : CHEST_HEARTS[chest][t]!, chances: CHEST_CHANCES[chest] };
}

/** End-of-day move for a final place in a group of `size`: +1 up, −1 down, 0 stays. */
export function leagueMovement(place: number, size: number, tier: number, points: number): -1 | 0 | 1 {
  if (place <= LEAGUE.promote && points > 0 && tier < LEAGUE_TIERS.length - 1) return 1;
  if (size >= LEAGUE.demoteMinGroup && place > size - LEAGUE.demote && tier > 0) return -1;
  return 0;
}
